import { useCallback, useRef, useState } from "react";
import { toast } from "@/hooks/use-toast";
import {
  CALCULATOR_FETCH_TIMEOUT_MS,
  postCalculatorApi,
  toUserFacingCalculatorError,
} from "@/utils/calculatorApiClient";

interface ApiResponse<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface EMIScheduleInput {
  principal: number;
  interestRate: number;
  tenureMonths: number;
  dateOfBirth?: string;
  prepayments?: Record<number, number>;
}

interface RetirementInput {
  currentAge: number;
  retirementAge: number;
  currentIncome: number;
  currentExpenses: number;
  currentSavings: number;
  monthlyInvestment: number;
  riskProfile: "conservative" | "moderate" | "aggressive";
  expectedLifespan: number;
}

interface LifeInsuranceInput {
  currentAge: number;
  annualIncome: number;
  currentExpenses: number;
  outstandingLoans: number;
  dependents: number;
  existingInsurance: number;
}

interface HealthInsuranceInput {
  age: number;
  familySize: number;
  cityTier: number;
  medicalHistory: string;
  existingCoverage: number;
}

interface EmergencyFundInput {
  monthlyExpenses: number;
  jobStability: "high" | "medium" | "low";
  dependents: number;
  hasInsurance: boolean;
  currentSavings: number;
  riskFactors: string[];
  incomeType: "salaried" | "business" | "freelance";
}

/**
 * Use Calculator Api.
 */
export const useCalculatorApi = <T>() => {
  const [state, setState] = useState<ApiResponse<T>>({
    data: null,
    loading: false,
    error: null,
  });
  const requestIdRef = useRef(0);

  /**
   * Execute — stable identity so consumers can safely put it in effect deps
   * without infinite recalculate loops (Home Loan toast/Calculating spam).
   */
  const execute = useCallback(
    async (endpoint: string, data: Record<string, unknown>): Promise<T | null> => {
      const requestId = ++requestIdRef.current;
      setState({ data: null, loading: true, error: null });

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), CALCULATOR_FETCH_TIMEOUT_MS);

        let result: T;
        try {
          result = await postCalculatorApi<T>(endpoint, data, controller.signal);
        } finally {
          clearTimeout(timeoutId);
        }

        if (requestId !== requestIdRef.current) {
          return result;
        }

        setState({ data: result, loading: false, error: null });
        return result;
      } catch (error) {
        if (requestId !== requestIdRef.current) {
          return null;
        }

        const isTimeout = error instanceof Error && error.name === "AbortError";
        let errorMessage = "Calculation failed";
        if (isTimeout) {
          errorMessage = "Calculator service timed out. Please try again.";
        } else if (error instanceof Error) {
          errorMessage = toUserFacingCalculatorError(error.message);
        }

        setState({ data: null, loading: false, error: errorMessage });

        toast({
          title: "Calculation Failed",
          description: errorMessage,
          variant: "destructive",
        });

        return null;
      }
    },
    []
  );

  return {
    ...state,
    execute,
  };
};

/**
 * Use EMICalculator.
 */
export const useEMICalculator = () => {
  const { data, loading, error, execute } = useCalculatorApi();

  const calculateEMI = useCallback(
    (principal: number, interestRate: number, tenureMonths: number) => {
      return execute("/emi/calculate", { principal, interestRate, tenureMonths });
    },
    [execute]
  );

  /**
   * Calculate EMISchedule.
   */
  const calculateEMISchedule = useCallback(
    (
      principal: number,
      interestRate: number,
      tenureMonths: number,
      dateOfBirth?: string,
      prepayments?: Record<number, number>
    ) => {
      return execute("/emi/schedule", {
        principal,
        interestRate,
        tenureMonths,
        dateOfBirth,
        prepayments,
      });
    },
    [execute]
  );

  return { data, loading, error, execute, calculateEMI, calculateEMISchedule };
};

export const useEmergencyFundCalculator = () => {
  const { data, loading, error, execute } = useCalculatorApi();

  const calculateEmergencyFund = useCallback(
    (inputs: EmergencyFundInput) => {
      return execute("/emergency-fund/calculate", inputs);
    },
    [execute]
  );

  return { data, loading, error, execute, calculateEmergencyFund };
};

export const useLifeInsuranceCalculator = () => {
  const { data, loading, error, execute } = useCalculatorApi();

  const calculateLifeInsurance = useCallback(
    (inputs: LifeInsuranceInput) => {
      return execute("/insurance/life/calculate", inputs);
    },
    [execute]
  );

  return { data, loading, error, execute, calculateLifeInsurance };
};

export const useHealthInsuranceCalculator = () => {
  const { data, loading, error, execute } = useCalculatorApi();

  const calculateHealthInsurance = useCallback(
    (inputs: HealthInsuranceInput) => {
      return execute("/insurance/health/calculate", inputs);
    },
    [execute]
  );

  return { data, loading, error, execute, calculateHealthInsurance };
};

/**
 * Use Retirement Calculator.
 */
export const useRetirementCalculator = () => {
  const { data, loading, error, execute } = useCalculatorApi();

  const calculateRetirement = useCallback(
    (inputs: RetirementInput) => {
      return execute("/retirement/calculate", inputs);
    },
    [execute]
  );

  const fetchRecommendations = useCallback(
    (inputs: RetirementInput) => {
      return execute("/retirement/recommendations", inputs);
    },
    [execute]
  );

  return { data, loading, error, execute, calculateRetirement, fetchRecommendations };
};

export type {
  EMIScheduleInput,
  RetirementInput,
  LifeInsuranceInput,
  HealthInsuranceInput,
  EmergencyFundInput,
};
